import { useState } from 'react';
import { Pencil } from 'lucide-react';
import type { GuidanceDto, JourneyStepDto } from '@ederest/contracts';
import type { GuidanceActions } from '../../../shared/api/queries.js';
import { GuidanceCard } from '../../../shared/guidance/GuidanceCard.js';
import { Button } from '../../../shared/ui/Button.js';
import { FrictionScore, NOTABLE_EXIT_RATE } from '../../../shared/ui/FrictionScore.js';
import './step-detail-panel.css';

interface StepDetailPanelProps {
  step: JourneyStepDto | null;
  steps: readonly JourneyStepDto[];
  guidances: readonly GuidanceDto[];
  languages: readonly string[];
  actions: GuidanceActions;
  onRenameStep: (name: string) => void;
}

/** Right-hand panel: the selected step's measurement and its guidance. */
export function StepDetailPanel({
  step,
  steps,
  guidances,
  languages,
  actions,
  onRenameStep,
}: StepDetailPanelProps) {
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState('');

  if (!step) {
    return (
      <aside className="detail detail--empty">
        <p>Sélectionnez une étape du parcours pour lire sa mesure et décider de ses guidances.</p>
      </aside>
    );
  }

  const stepGuidances = guidances.filter((g) => g.stepId === step.id);
  const proposed = stepGuidances.filter((g) => g.state === 'proposed' || g.state === 'approved');
  const live = stepGuidances.filter((g) => g.state === 'published' || g.state === 'disabled');
  const dismissed = stepGuidances.filter((g) => g.state === 'dismissed');

  return (
    <aside className="detail">
      <header className="detail__head">
        {renaming ? (
          <form
            className="detail__rename"
            onSubmit={(event) => {
              event.preventDefault();
              onRenameStep(draftName.trim() || step.name);
              setRenaming(false);
            }}
          >
            <input
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              autoFocus
              maxLength={120}
            />
            <Button variant="primary" type="submit">
              Renommer
            </Button>
          </form>
        ) : (
          <>
            <h2 className="detail__title">{step.name}</h2>
            <Button
              variant="ghost"
              icon={<Pencil />}
              aria-label="Renommer l'étape"
              onClick={() => {
                setDraftName(step.name);
                setRenaming(true);
              }}
            />
          </>
        )}
      </header>

      <p className="detail__path">{step.pagePathPattern}</p>

      <dl className="detail__metrics">
        <Metric label="Usagers" value={step.sessionCount.toLocaleString('fr-FR')} />
        <Metric
          label="Ont quitté ici"
          value={`${Math.round(step.dropOffRate * 100)} %`}
          alert={step.dropOffRate >= NOTABLE_EXIT_RATE}
        />
        <Metric label="Durée médiane" value={formatDuration(step.medianDurationMs)} />
      </dl>

      <div className="detail__friction">
        <span className="detail__friction-label">Score de friction</span>
        <FrictionScore score={step.frictionScore} bar />
      </div>

      {step.staleSince && (
        <p className="detail__stale">
          Cette étape n'apparaît plus dans la dernière analyse. Elle est conservée, jamais supprimée
          automatiquement.
        </p>
      )}

      <Section
        title="Guidances proposées"
        count={proposed.length}
        hint="Générées automatiquement à partir de la mesure"
      >
        {proposed.length === 0 ? (
          <p className="detail__empty">Aucune proposition en attente sur cette étape.</p>
        ) : (
          proposed.map((guidance) => (
            <GuidanceCard
              key={guidance.id}
              guidance={guidance}
              languages={languages}
              steps={steps}
              actions={actions}
            />
          ))
        )}
      </Section>

      {live.length > 0 && (
        <Section title="Guidances en ligne" count={live.length}>
          {live.map((guidance) => (
            <GuidanceCard
              key={guidance.id}
              guidance={guidance}
              languages={languages}
              steps={steps}
              actions={actions}
            />
          ))}
        </Section>
      )}

      {dismissed.length > 0 && (
        <Section title="Guidances écartées" count={dismissed.length} hint="Non reproposées à l'identique">
          {dismissed.map((guidance) => (
            <GuidanceCard
              key={guidance.id}
              guidance={guidance}
              languages={languages}
              steps={steps}
              actions={actions}
            />
          ))}
        </Section>
      )}
    </aside>
  );
}

function Metric({ label, value, alert = false }: { label: string; value: string; alert?: boolean }) {
  return (
    <div className="detail__metric">
      <dt className="detail__metric-label">{label}</dt>
      <dd className={`detail__metric-value${alert ? ' detail__metric-value--alert' : ''}`}>{value}</dd>
    </div>
  );
}

function Section({
  title,
  count,
  hint,
  children,
}: {
  title: string;
  count: number;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="detail__section">
      <header className="detail__section-head">
        <h3>{title}</h3>
        <span className="detail__section-count">{count}</span>
      </header>
      {hint && <p className="detail__section-hint">{hint}</p>}
      {children}
    </section>
  );
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds} s`;
  return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
}
